<template>
	<div class="modal">
		<div class="row h-0">
			<div class="col-md-12 end-sm end-md end-lg">
				<a class="pointer-events-auto absolute right-12 z-[999]" @click="close()"
					><span class="fa-light fa-circle-xmark textXlg textGrey"></span
				></a>
			</div>
		</div>
		<div class="flex flex-col gap-2">
			<div class="col-md-12 flex w-full items-center justify-center gap-4">
				<p class="textGrey textLg textCenter">
					Call Flow
					<span class="font-bold">
						{{
							realtime
								? ' - ' + moment(callData.startTime).format('DD/MM/YYYY HH:mm:ss')
								: ' - ' + moment(allPackets[0]?.createdAt).format('DD/MM/YYYY HH:mm:ss')
						}}
					</span>
				</p>
				<button
					class="z[2] flex flex-row flex-nowrap items-center gap-2 px-4 py-2 font-bold text-offmain dark:text-white"
					@click="exportFlow"
				>
					<i class="fa-light fa-arrow-down-to-line mr-2"></i>
					Export to HTML
				</button>
			</div>
		</div>
		<div v-if="callData && allPackets.length > 0">
			<p class="mb-4 text-center text-lg font-semibold"></p>
			<div class="realtime-header">
				<p v-if="callData.leg" class="uppercase">
					<strong class="font-bold normal-case">Leg:</strong> {{ callData.leg }}
				</p>
				<p v-if="callData.a_call_from || callData.callFrom">
					<strong class="font-bold">From:</strong> {{ realtime ? callData.callFrom : callData.a_call_from }}
				</p>
				<p v-if="callData.call_to || callData.callTo">
					<strong class="font-bold">To:</strong> {{ realtime ? callData.callTo : callData.call_to }}
				</p>
				<p v-if="callData.a_duration || callData.connectedDuration">
					<strong class="font-bold">Duration:</strong>
					{{ realtime ? callData.connectedDuration : callData.a_duration }}s
				</p>
				<p v-if="callData.carrier_name">
					<strong class="font-bold">Carrier:</strong> {{ callData.carrier_name }}
				</p>
				<p v-if="callData.tenant_name || callData.tenantName">
					<strong class="font-bold">Tenant:</strong>
					{{ realtime ? callData.tenantName : callData.tenant_name }}
				</p>
				<p v-if="callData.zone_name"><strong class="font-bold">Zone:</strong> {{ callData.zone_name }}</p>
				<p v-if="allPackets.length > 0 || callData.connectedTime">
					<strong class="font-bold">Start Time:</strong>
					{{
						realtime
							? moment(callData.connectedTime || callData.startTime).format('HH:mm:ss.SSS')
							: moment(allPackets[0]?.createdAt).format('HH:mm:ss.SSS')
					}}
				</p>
				<p v-if="allPackets.length > 0">
					<strong class="font-bold">End Time:</strong>
					{{ moment(allPackets[allPackets.length - 1]?.createdAt).format('HH:mm:ss.SSS') }}
				</p>
			</div>
		</div>
		<div class="flex flex-col items-center justify-center gap-4 pt-4">
			<div
				v-if="allPackets.length > 0"
				class="relative mx-auto flex w-full items-center justify-center overflow-x-auto rounded-lg bg-white p-4 dark:bg-offmain"
				:style="{ 'min-height': `${allPackets.length * 54 + 72}px` }"
			>
				<table class="absolute left-8 top-0 h-full min-w-[840px] py-4 md:relative">
					<thead>
						<th
							v-for="endpoint in call.endpoints"
							:key="endpoint.ip"
							class="relative w-80 cursor-pointer"
							@click="displayIP(endpoint.ip)"
						>
							<div
								class="z-[910] rounded-lg bg-white py-1 hover:bg-offwhite dark:bg-offmain dark:hover:bg-offmaindark"
							>
								<p>
									{{ endpoint.ip }}
									<span
										v-if="endpoint.whois.whoisData.country"
										class="text-xs opacity-40"
										:style="`(${endpoint.whois.whoisData.country})`"
									>
										{{ endpoint.whois.whoisData.country }}
									</span>
								</p>
								<p v-if="endpoint.whois.whoisData.netname" class="text-xs uppercase opacity-40">
									{{ endpoint.whois.whoisData.netname }}
								</p>
							</div>
							<div
								class="absolute left-1/2 w-0.5 rounded-full bg-main/50 dark:bg-main"
								:style="`height: ${allPackets.length * 54}px;`"
							></div>
							<OnClickOutside @trigger="selectedIp = null">
								<div
									v-if="selectedIp == endpoint.ip"
									class="absolute left-0 top-[48px] z-[950] w-full cursor-auto rounded-md border border-offmain bg-offwhite p-2 text-start font-normal dark:border-white dark:bg-offmaindark"
								>
									<p class="pb-2 font-semibold">{{ endpoint.ip }}</p>
									<div v-if="Object.keys(endpoint.whois.whoisData).length > 2">
										<p v-if="endpoint.whois.whoisData.country && endpoint.whois.whoisData.inetnum">
											{{ endpoint.whois.whoisData.country || 'N/A' }} -
											{{ endpoint.whois.whoisData.inetnum || '' }}
										</p>
										<p v-if="endpoint.whois.whoisData.descr || endpoint.whois.whoisData.person">
											{{
												endpoint.whois.whoisData.descr || endpoint.whois.whoisData.person || ''
											}}
										</p>
										<p v-if="endpoint.whois.whoisData.origin">
											Origin: {{ endpoint.whois.whoisData.origin }}
										</p>
										<p v-if="endpoint.whois.whoisData.netname">
											{{ endpoint.whois.whoisData.netname || '' }}
										</p>
										<p v-if="endpoint.whois.whoisData.created" class="pt-2 text-xs opacity-40">
											Registered {{ moment(endpoint.whois.whoisData.created).fromNow() }}
										</p>
									</div>
									<div v-else>
										<p>No data</p>
									</div>
								</div>
							</OnClickOutside>
						</th>
					</thead>
					<tbody>
						<tr v-for="(p, i) in allPackets" :key="i">
							<td v-if="calculateFromTo(p).from" :colspan="calculateFromTo(p).from"></td>
							<td class="text-center" :colspan="calculateFromTo(p).span + 1">
								<div class="relative mx-auto flex w-full flex-col">
									<Teleport to="body">
										<OnClickOutside v-if="selectedPacket" @trigger="selectedPacket = null">
											<div
												v-if="selectedPacketState"
												class="absolute z-[950] w-max max-w-[min(80vw,max)] -translate-x-1/4 cursor-auto rounded-md border border-offmain bg-offwhite px-2 py-0.5 text-start dark:border-white dark:bg-offmaindark"
												:style="{
													top: selectedPacketPosition.top + 'px',
													left: selectedPacketPosition.left + 'px'
												}"
											>
												<p class="font-bold">
													{{ selectedPacket.src }} -> {{ selectedPacket.dst }}
												</p>
												<p class="py-1">
													SIP/2.0
													<span class="font-bold text-orange">
														{{ selectedPacket.status || selectedPacket.method }}
														{{ selectedPacket.reason || '' }}
													</span>
												</p>
												<p class="font-bold underline">
													{{ moment(p.createdAt).format('YYYY-MM-DD HH:mm:ss.SSS') }}
												</p>
												<p
													v-for="key in Object.keys(selectedPacket.headers)"
													:key="key"
													class="truncate whitespace-normal"
													:class="key == 'via' ? 'max-w-lg' : ''"
												>
													<span class="font-bold">{{ $global.prettyPrint(key) }}: </span
													>{{ selectedPacket.headers[key] }}
												</p>
												<p
													v-if="selectedPacket.body"
													class="font-serif whitespace-pre pt-2 text-sm"
												>
													{{ selectedPacket.body }}
												</p>
											</div>
										</OnClickOutside>
									</Teleport>
									<div
										:id="'packet-' + i"
										class="mx-auto"
										:style="{ width: 320 * calculateFromTo(p).span - 1 + 'px' }"
										@click="displayPacket(p, i, $event)"
									>
										<div
											class="group relative mx-auto cursor-pointer rounded-md bg-white p-1 hover:bg-offwhite dark:bg-offmain dark:hover:bg-offmaindark"
											:style="{ width: `calc(100% - 64px)` }"
										>
											<div class="z-[915] flex flex-wrap items-center justify-center gap-2">
												<p class="text-md font-medium">
													{{ p.method || p.status }} {{ p.reason || '' }}
													{{
														p.headers['content-type']
															?.replace('application/', '')
															.toUpperCase() || ''
													}}
												</p>
											</div>
											<svg
												class="mx-auto h-5 w-full fill-main stroke-main dark:fill-offwhite dark:stroke-offwhite"
												:class="calculateFromTo(p).isReverse ? 'rotate-180 transform' : ''"
												viewBox="0 0 110 5"
												preserveAspectRatio="none"
											>
												<defs>
													<marker
														id="arrowhead"
														markerWidth="10"
														markerHeight="7"
														refX="0"
														refY="3.5"
														orient="auto"
													>
														<polygon points="0 0, 7 3.5, 0 7" />
													</marker>
												</defs>
												<line
													x1="2"
													y1="2"
													x2="105"
													y2="2"
													stroke-width="0.5"
													marker-end="url(#arrowhead)"
												/>
											</svg>
											<p
												class="absolute bottom-1 rounded-full bg-white px-1 text-sm font-light group-hover:bg-offwhite dark:bg-offmain dark:group-hover:bg-offmaindark"
												:style="{ left: `calc(50% - 37px)` }"
											>
												{{ moment(p.createdAt).format('HH:mm:ss.SSS') }}
											</p>
										</div>
									</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div v-else class="flex w-full items-center justify-center bg-white py-4 dark:bg-offmain">
				<h2 class="text-xl font-semibold">{{ loading ? 'Loading...' : 'Sorry, this call is unavailable' }}</h2>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment'
import cdrsApi from '@/scripts/calldetailrecords/calldetailrecords.api'
import realtimeApi from '@/scripts/realtime/realtime.api'
import { OnClickOutside } from '@vueuse/components'

export default {
	components: {
		OnClickOutside
	},
	inject: ['user', 'userRoles'],
	props: ['callId', 'callData', 'refreshInterval', 'realtime'],

	data() {
		return {
			allPackets: [],
			moment,
			selectedPacket: null,
			selectedPacketState: false,
			selectedPacketPosition: { top: 0, left: 0 },
			selectedIp: null,
			call: null,
			loading: true,
			interval: null
		}
	},
	mounted() {
		document.addEventListener('keyup', this.onKeyUp)
		this.refresh()

		if (parseInt(this.refreshInterval)) {
			this.interval = setInterval(() => {
				this.refresh()
			}, parseInt(this.refreshInterval))
		}
	},
	beforeUnmount() {
		document.removeEventListener('keyup', this.onKeyUp)
		clearInterval(this.interval)
	},

	methods: {
		async refresh() {
			if (!this.callId) return

			try {
				let newCall = null

				if (this.realtime) newCall = await realtimeApi.getSipCaptureById(this.callId)
				else newCall = await cdrsApi.getSipCaptureById(this.callId)

				if (newCall) {
					this.call = newCall
				} else if (this.interval) clearInterval(this.interval)

				if (this.call) {
					const newPackets = this.getAllPacketsInOrder(this.call)
					if (newPackets?.length && !this.allPackets.length !== newPackets.length) {
						this.allPackets = newPackets
					}
				}
				this.loading = false
			} catch (err) {
				console.log(err)
				this.allPackets = []
				this.loading = false
			}
		},
		getAllPacketsInOrder(call) {
			let packets = []
			call.captures.forEach((leg) => {
				leg.packets.forEach((packet) => {
					packets.push(packet)
				})
			})
			return packets.sort((a, b) => a.createdAt - b.createdAt)
		},
		displayPacket(packet, i) {
			this.selectedPacketState = true
			this.selectedPacket = packet
			const rect = document.getElementById(`packet-${i}`).getBoundingClientRect()
			this.selectedPacketPosition = {
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX
			}
		},
		displayIP(ip) {
			if (this.selectedIp == ip) {
				this.selectedIp = null
				return
			}
			this.selectedIp = ip
		},
		calculateFromTo(packet) {
			let from = this.call.endpoints.findIndex((e) => e.ip == packet.src)
			let to = this.call.endpoints.findIndex((e) => e.ip == packet.dst)
			let span = Math.abs(from - to)
			let isReverse = from > to
			if (from > to) {
				let temp = from
				from = to
				to = temp
			}
			return { from, to, span, isReverse }
		},
		close() {
			this.$emit('close')
		},
		onKeyUp(event) {
			if (event.key == 'Escape') this.$emit('close')
		},
		copyToClipboard(text) {
			navigator.clipboard.writeText(text)
			this.$swal({
				title: 'Copied to clipboard',
				icon: 'success',
				toast: true,
				position: 'bottom-start',
				timer: 1500,
				timerProgressBar: true,
				showConfirmButton: false
			})
		},
		prepareForCode(value) {
			if (Array.isArray(value)) {
				return value.map((v) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
			}
			return value && value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		},
		exportFlow() {
			let htmlContent = `
				<!DOCTYPE html>
				<html>
				<head>
					<meta charset="UTF-8">
					<title>Call Flow Export</title>
					<style>
						body {
							font-family: Arial, sans-serif;
							background-color: #f9f9f9;
							padding: 20px;
						}
						h2 {
							text-align: center;
							color: #333;
						}
						.info-container {
							display: flex;
							flex-wrap: wrap;
							justify-content: center;
							gap: 10px;
							margin-top: 20px;
						}
						.info-item {
							background-color: #f1f1f1;
							padding: 10px;
							border-radius: 5px;
							text-align: center;
							flex: 1 1 200px;
						}
						table {
							width: 100%;
							border-collapse: collapse;
							margin-top: 20px;
						}
						th, td {
							border: 1px solid #ddd;
							padding: 4px;
							text-align: center;
						}
						th {
							background-color: #4CAF50;
							color: white;
						}
						tr:nth-child(even){background-color: #f2f2f2;}
						tr:hover {background-color: #ddd;}
						details {
							text-align: left;
						}
						summary {
							cursor: pointer;
							font-weight: bold;
							margin-top: 5px;
						}
						.extra-info {
							margin-top: 10px;
							background-color: #f1f1f1;
							padding: 10px;
							border-radius: 5px;
						}
						.sdp-body {
							white-space: pre-wrap;
						}
						.arrow {
							width: 100%;
							height: 10px;
							position: relative;
						}
						.arrow svg {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
						}
					</style>
				</head>
				<body>
					<h2>Call Flow Export</h2>
					<div class="info-container">
						<div class="info-item"><strong>From:</strong> ${this.realtime ? this.callData.callFrom : this.callData.a_call_from || 'N/A'}</div>
						<div class="info-item"><strong>To:</strong> ${this.realtime ? this.callData.callTo : this.callData.call_to || 'N/A'}</div>
						<div class="info-item"><strong>Duration:</strong> ${this.realtime ? this.callData.connectedDuration : this.callData.a_duration || 'N/A'}s</div>
						<div class="info-item"><strong>Zone:</strong> ${this.callData.zone_name || 'N/A'}</div>
						<div class="info-item"><strong>Start Time:</strong> ${
							this.realtime
								? moment(this.callData.startTime).format('DD/MM/YYYY HH:mm:ss')
								: moment(this.allPackets[0].createdAt).format('DD/MM/YYYY HH:mm:ss')
						}</div>
						<div class="info-item"><strong>End Time:</strong> ${moment(
							this.allPackets[this.allPackets.length - 1].createdAt
						).format('DD/MM/YYYY HH:mm:ss')}</div>
					</div>
					<table>
						<thead>
							<tr>
								${this.call.endpoints.map((endpoint) => `<th>${endpoint.ip}</th>`).join('')}
							</tr>
						</thead>
						<tbody>
							${this.allPackets
								.map((p, i) => {
									const fromTo = this.calculateFromTo(p)
									return `
								<tr>
									${fromTo.from > 0 ? `<td colspan="${fromTo.from}"></td>` : ''}
									<td colspan="${fromTo.span + 1}" style="background-color: ${fromTo.isReverse ? '#ffcccc' : '#ccffcc'};">
										<div class="arrow">
											<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 10">
												<line x1="${fromTo.isReverse ? '100' : '0'}" y1="5" x2="${fromTo.isReverse ? '0' : '100'}" y2="5" stroke="${fromTo.isReverse ? 'red' : 'green'}" stroke-width="2"/>
												<polygon points="${fromTo.isReverse ? '10,0 0,5 10,10' : '90,0 100,5 90,10'}" fill="${fromTo.isReverse ? 'red' : 'green'}"/>
											</svg>
										</div>
										<strong>${p.method || p.status}</strong> ${p.reason || ''}
										<br>
										<em>${typeof p.headers['content-type'] === 'string' ? p.headers['content-type'].replace('application/', '').toUpperCase() : ''}</em>
										<br>
										<small>${moment(p.createdAt).format('YYYY-MM-DD HH:mm:ss.SSS')}</small>
										<details>
											<summary>View Packet</summary>
											<div class="extra-info">
												<p><strong>Source:</strong> ${p.src}</p>
												<p><strong>Destination:</strong> ${p.dst}</p>
												<p><strong>Headers:</strong></p>
												<ul>
													${Object.entries(p.headers)
														.map(
															([key, value]) =>
																`<li><strong>${key.charAt(0).toUpperCase() + key.slice(1)}:</strong>${this.prepareForCode(value)}</li>`
														)
														.join('')}
												</ul>
												${p.body ? `<div class="sdp-body"><strong>Body:</strong> <pre>${p.body}</pre></div>` : ''}
											</div>
										</details>
									</td>
								</tr>
								`
								})
								.join('')}
						</tbody>
					</table>
				</body>
				</html>
			`

			const blob = new Blob([htmlContent], { type: 'text/html' })
			const url = URL.createObjectURL(blob)
			const downloadAnchorNode = document.createElement('a')
			downloadAnchorNode.setAttribute('href', url)
			downloadAnchorNode.setAttribute('download', `call_flow_${this.callId}.html`)
			document.body.appendChild(downloadAnchorNode)
			downloadAnchorNode.click()
			downloadAnchorNode.remove()
			URL.revokeObjectURL(url)
		}
	}
}
</script>

<style scoped>
::-webkit-scrollbar {
	width: 0px !important;
}

.realtime-header {
	@apply flex flex-wrap items-center justify-center gap-x-8 gap-y-4 rounded-lg bg-white p-4 text-black dark:bg-offmain dark:text-white;
}

.realtime-header p {
	@apply whitespace-nowrap rounded-sm bg-black/5 px-4 py-2 text-sm font-semibold dark:bg-white/5;
}
</style>
